// ============================================================================
// Das Band der Abschnitte, und der Chip, der sagt, wo man gerade ist.
//
// ORIGINALTEXT MARVIN (03.09.2026)
//   „auch bei den anderen seiten soll das menüband oben stehen bleiben wenn
//    die seite durchgescrollt wird. er soll den menüpunkt als chip einfärben
//    wenn man beim scrollen in dem bereich ist"
//
// EIN BILDSCHIRM MIT ZEHN KARTEN UNTEREINANDER IST EINE ROLLE OHNE
// INHALTSVERZEICHNIS. In der Konfiguration liegen unter einem Reiter bis zu
// sechs Bereiche, im Systemzustand zehn Karten: wer den dritten sucht, rollt
// und liest Überschriften. Das Band nennt sie alle in einer Zeile, springt
// hin - und färbt den ein, in dem man gerade steht.
//
// ES IST EINE ÜBERSICHT UND KEIN ZWEITER WEG. Die Karten bleiben, wo sie
// sind, und es verschwindet nichts: ein Band, das Abschnitte AUSBLENDET,
// wäre ein Reiter, und Reiter gibt es hier schon.
// ============================================================================

import { useEffect, useRef, useState } from 'react';

/**
 * Aus einer Überschrift eine Kennung machen.
 *
 * EINE KENNUNG IST EIN BEZEICHNER UND KEIN TEXT FÜR MENSCHEN - sie läuft
 * durch `getElementById` und durch einen Auswahlausdruck, und dort sind
 * Leerzeichen und Umlaute genau die Zeichen, die stolpern. Der Titel daneben
 * behält seine Umlaute, wie überall in diesem Haus.
 */
export function kennung(text: string): string {
  return String(text ?? '')
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'abschnitt';
}

export interface Abschnitt {
  /** Die Kennung des Elements, zu dem gesprungen wird. */
  id: string;
  /** Was auf dem Chip steht. */
  titel: string;
  /** Eine Zahl daneben, wo eine etwas sagt. */
  zahl?: number;
}

/**
 * WELCHER ABSCHNITT GERADE DASTEHT.
 *
 * GEMESSEN WIRD DIE LAGE ZUR OBERKANTE UND NICHT DER GRÖSSTE SICHTBARE
 * ANTEIL. Der naheliegende Weg über `IntersectionObserver` und
 * `intersectionRatio` gibt bei einer Karte, die höher ist als das Fenster,
 * IMMER den grössten Anteil - und dann klebt der Chip an ihr, auch wenn ihr
 * Ende längst oben hinausgelaufen ist. Gesucht wird der LETZTE Abschnitt,
 * dessen Oberkante noch über der Lesekante liegt: genau der, den man gerade
 * liest.
 *
 * DIE LESEKANTE IST NICHT DIE FENSTERKANTE. Über ihr klebt der Kopf mit
 * seinen Leisten; ein Abschnitt, der dahinter liegt, ist nicht zu sehen.
 * Deshalb `--klebehoehe` (siehe `teile/klebeband.ts`) und ein Finger breit
 * darunter.
 */
export function useAbschnitt(ids: string[]): string | null {
  const [jetzt, setJetzt] = useState<string | null>(ids[0] ?? null);
  // Die Kennungen als TEXT in der Abhängigkeit: das Feld ist bei jedem
  // Zeichnen ein neues Objekt, und ein Effekt darauf meldete sich bei jedem
  // Zeichnen neu an. Dieselbe Falle wie in `useEntwurf` (31.08.2026).
  const schluessel = ids.join('|');

  useEffect(() => {
    const liste = schluessel ? schluessel.split('|') : [];
    if (!liste.length) { setJetzt(null); return; }

    const buehne = document.querySelector<HTMLElement>('.buehne');
    if (!buehne) return;

    let angefordert = 0;
    const messen = () => {
      angefordert = 0;
      const oben = buehne.getBoundingClientRect().top
        + (parseFloat(getComputedStyle(buehne).getPropertyValue('--klebehoehe'))
           || 0) + 12;
      let treffer = liste[0];
      for (const id of liste) {
        const e = document.getElementById(id);
        if (!e) continue;
        if (e.getBoundingClientRect().top <= oben) treffer = id;
      }
      /* UND GANZ UNTEN GEWINNT DER LETZTE. Ein kurzer letzter Abschnitt
         erreicht die Lesekante nie, wenn die Seite vorher zu Ende ist -
         dann bliebe sein Chip für immer blass, obwohl man ihn ansieht. */
      if (buehne.scrollTop + buehne.clientHeight >= buehne.scrollHeight - 4) {
        treffer = liste[liste.length - 1];
      }
      setJetzt(treffer);
    };
    const bald = () => {
      if (angefordert) return;
      angefordert = requestAnimationFrame(messen);
    };

    bald();
    buehne.addEventListener('scroll', bald, { passive: true });
    const gr = new ResizeObserver(bald);
    gr.observe(buehne);
    return () => {
      if (angefordert) cancelAnimationFrame(angefordert);
      buehne.removeEventListener('scroll', bald);
      gr.disconnect();
    };
  }, [schluessel]);

  return jetzt;
}

/**
 * DIE ABSCHNITTE AUS DEM BILDSCHIRM SELBST LESEN.
 *
 * Für Seiten, deren Karten einzeln und unter Bedingungen dastehen - der
 * Systemzustand hat zwölf, und die halbe Hälfte hängt daran, ob es etwas zu
 * melden gibt. **Eine zweite Liste „so heissen die Karten" neben dem
 * Bildschirm liefe beim ersten Nachtrag auseinander**, und dann stünde im
 * Band ein Chip, der auf nichts zeigt, oder eine Karte hätte keinen. Gelesen
 * wird deshalb, was WIRKLICH dasteht: jedes Element mit `data-abschnitt`.
 */
export function useAbschnitteAusDom(): Abschnitt[] {
  const [liste, setListe] = useState<Abschnitt[]>([]);
  useEffect(() => {
    const buehne = document.querySelector<HTMLElement>('.buehne');
    if (!buehne) return;
    let angefordert = 0;
    const lesen = () => {
      angefordert = 0;
      const neu = Array.from(
        buehne.querySelectorAll<HTMLElement>('.inhalt [data-abschnitt]'))
        .filter((e) => e.id)
        .map((e) => ({ id: e.id, titel: e.dataset.abschnitt ?? e.id }));
      setListe((alt) =>
        JSON.stringify(alt) === JSON.stringify(neu) ? alt : neu);
    };
    const bald = () => {
      if (angefordert) return;
      angefordert = requestAnimationFrame(lesen);
    };
    bald();
    const mu = new MutationObserver(bald);
    mu.observe(buehne, { childList: true, subtree: true });
    return () => {
      if (angefordert) cancelAnimationFrame(angefordert);
      mu.disconnect();
    };
  }, []);
  return liste;
}

/**
 * Das Band selbst.
 *
 * Es steht NUR da, wenn es mehr als einen Abschnitt gibt: ein
 * Inhaltsverzeichnis mit einer Zeile ist keines, sondern eine zweite
 * Überschrift.
 */
export function Abschnittsband({ abschnitte }: { abschnitte: Abschnitt[] }) {
  const jetzt = useAbschnitt(abschnitte.map((a) => a.id));
  const band = useRef<HTMLDivElement>(null);

  /* DER GEWÄHLTE CHIP MUSS IM BAND ZU SEHEN SEIN. Auf dem Telefon rollt das
     Band seitlich; ohne das hier stünde der eingefärbte Chip beim Rollen
     ausserhalb, und es sähe aus, als sei gar keiner gefärbt. */
  useEffect(() => {
    if (!jetzt || !band.current) return;
    const k = band.current.querySelector<HTMLElement>(`[data-fuer="${jetzt}"]`);
    if (!k) return;
    const l = band.current.getBoundingClientRect();
    const c = k.getBoundingClientRect();
    if (c.left < l.left || c.right > l.right) {
      band.current.scrollTo({ left: k.offsetLeft - 12, behavior: 'smooth' });
    }
  }, [jetzt]);

  if (abschnitte.length < 2) return null;

  return (
    <div className="abschnittsband" ref={band}>
      {abschnitte.map((a) => (
        <button key={a.id} type="button" data-fuer={a.id}
                aria-current={jetzt === a.id}
                onClick={() => {
                  const e = document.getElementById(a.id);
                  /* `scroll-margin-top` steht am Ziel und rechnet mit
                     `--klebehoehe`: ohne es landet der Abschnitt UNTER den
                     klebenden Leisten, und der Sprung sieht aus, als hätte
                     er nichts getan. */
                  e?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}>
          {a.titel}
          {a.zahl !== undefined && <span className="reiterzahl">{a.zahl}</span>}
        </button>
      ))}
    </div>
  );
}
